import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RUOLI } from "@/lib/constants";
import { Plus, User, Trash2, Upload } from "lucide-react";
import { BulkImportDialog } from "@/components/BulkImportDialog";

const ROLE_LABEL = { dipendente: "Dipendente", responsabile: "Responsabile", amministratore: "Amministratore" };
const EMPTY = { name: "", email: "", password: "", role: "dipendente", company: "COSMA", group: "" };

export default function Users() {
  const { user, activeCompany } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = user.role === "amministratore";
  const effectiveCompany = activeCompany || user.active_company || user.company;
  const isReadOnly = effectiveCompany === "TUTTE";

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await api.post("/users", form); toast.success("Utente creato"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Utenti</h1>
          <p className="text-slate-500 mt-1">{users.length} utenti registrati.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <BulkImportDialog entity="users" disabled={isReadOnly} onComplete={load} trigger={<Button data-testid="import-users-btn" disabled={isReadOnly} variant="outline"><Upload size={17} className="mr-1.5" /> Importa dipendenti</Button>} />}
          {isAdmin && !isReadOnly && <Button data-testid="add-user-btn" onClick={() => { setForm({ ...EMPTY, company: effectiveCompany }); setOpen(true); }} className="bg-[#E30613] hover:bg-[#C50510] font-semibold"><Plus size={18} className="mr-1.5" /> Nuovo utente</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-3 sm:px-5 py-3">Nome</th><th className="text-left px-3 sm:px-5 py-3">Email</th><th className="hidden sm:table-cell text-left px-5 py-3">Ruolo</th><th className="hidden sm:table-cell text-left px-5 py-3">Azienda</th><th className="hidden sm:table-cell px-5 py-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-50" data-testid={`user-${u.id}`}>
                <td className="px-3 sm:px-5 py-3 font-medium"><div className="flex items-center gap-2 min-w-0"><div className="h-7 w-7 shrink-0 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">{u.name?.[0]}</div><span className="truncate">{u.name}</span></div></td>
                <td className="px-3 sm:px-5 py-3 text-slate-600 break-all">{u.email}</td>
                <td className="hidden sm:table-cell px-5 py-3"><span className="text-xs font-medium bg-slate-100 rounded-full px-2.5 py-0.5">{ROLE_LABEL[u.role]}</span></td>
                <td className="hidden sm:table-cell px-5 py-3 text-slate-600">{u.company || "—"}</td>
                <td className="hidden sm:table-cell px-5 py-3 text-right">
                  {isAdmin && !isReadOnly && u.id !== user.id && (
                    <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input data-testid="user-password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{RUOLI.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Azienda</Label>
                <Input data-testid="user-company" value={effectiveCompany} disabled />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-user-btn" onClick={save} className="bg-[#E30613] hover:bg-[#C50510]">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
